import { ArrowRight, Shield, Clock, CheckCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export const HeroSection = ({ onGetStarted }) => {
  const features = [
    { icon: Shield, text: "Secure & Confidential" },
    { icon: Clock, text: "Response Within 24 Hours" },
    { icon: CheckCircle, text: "No Obligation" },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1760520830355-e6be53e41c2f?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/60" />
      
      {/* Content */}
      <div className="relative container mx-auto px-4 md:px-8 max-w-7xl pt-28 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="max-w-xl animate-slideIn">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_intake-hub-6/artifacts/l9bs5fno_Cletis%20Logo.png"
                alt="Red River Internet Auto Sales"
                className="h-32 md:h-40 w-auto object-contain"
                data-testid="hero-logo"
              />
            </div>

            {/* Headline */}
            <h1 
              className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-slate-900 leading-none mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="hero-title"
            >
              Find Your<br />
              <span className="text-[#A52A2A]">Perfect</span> Ride
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
              Tell us what you're looking for and let our team find the perfect vehicle for you. 
              Fast, easy, and no pressure.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                onClick={onGetStarted}
                className="bg-[#A52A2A] hover:bg-[#8B2323] text-white px-8 py-6 text-lg font-bold uppercase tracking-wide shadow-[0_4px_14px_0_rgba(213,0,0,0.39)] hover:shadow-[0_6px_20px_rgba(213,0,0,0.35)] transition-all transform hover:-translate-y-0.5"
                data-testid="get-started-btn"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <a
                href="tel:+13467187187"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-900 font-bold uppercase tracking-wide rounded-md transition-all"
                data-testid="hero-call-us-btn"
              >
                <Phone className="w-4 h-4" />
                Call Us (346) 718-7187
              </a>
            </div>

            {/* Trust Features */}
            <div className="flex flex-wrap gap-4">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 text-slate-500"
                >
                  <feature.icon className="w-4 h-4 text-[#A52A2A]" />
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Stats/Info Cards */}
          <div className="hidden lg:grid grid-cols-2 gap-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-100 transform hover:scale-105 transition-transform">
              <p className="text-4xl font-black text-[#A52A2A] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>500+</p>
              <p className="text-slate-600 font-medium">Vehicles Available</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-100 transform hover:scale-105 transition-transform mt-8">
              <p className="text-4xl font-black text-[#A52A2A] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>24hr</p>
              <p className="text-slate-600 font-medium">Response Time</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-100 transform hover:scale-105 transition-transform -mt-4">
              <p className="text-4xl font-black text-[#A52A2A] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>100%</p>
              <p className="text-slate-600 font-medium">Satisfaction Guarantee</p>
            </div>
            <div className="bg-[#111111] rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform mt-4">
              <p className="text-white text-sm font-semibold uppercase tracking-wider mb-2">Trade-In Welcome</p>
              <p className="text-gray-400 text-sm">Get top dollar for your current vehicle</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L48 110C96 100 192 80 288 70C384 60 480 60 576 65C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z" fill="#F4F4F5"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
